import { c as _c } from "react/compiler-runtime";
import { Bar, BarChart as RechartBarChart, CartesianGrid, Rectangle, ReferenceArea, Tooltip, XAxis, YAxis } from 'recharts';
import { useTheme } from 'styled-components';
import Chart, { ChartDataSet, ChartProps, ChartSampleCursor, CLASS_PREFIX, formatActiveNameForDom } from './Chart';
import { formatChartAxisNumber, getYAxisTicks } from './format';
const CustomBar = ({
  id,
  dataset,
  theme_colors
}: {
  id: string;
  dataset: ChartDataSet;
  theme_colors: any;
}) => props => {
  return <Rectangle {...props} ref={null} className={`${CLASS_PREFIX}-${id} ${CLASS_PREFIX}-${id}-${formatActiveNameForDom(dataset.key)} ${CLASS_PREFIX}-${id}-${formatActiveNameForDom(props.name)}`} strokeOpacity={0} style={{
    transition: 'opacity 300ms'
  }} fill={dataset.theme_color ? theme_colors.surface.chart[dataset.theme_color] : dataset.hex_color} />;
};
interface BarChartProps extends Omit<ChartProps, 'type' | 'children'> {
  datasets_type?: 'stacked' | 'switch';
}
const BarChart: React.FC<BarChartProps> = props => {
  const $ = _c(7);
  const theme = useTheme();
  let t0;
  if ($[0] !== props.datasets_type || $[1] !== theme.colors || $[2] !== theme.font_sizes.xs.font_size) {
    t0 = t1 => {
      const {
        id,
        data,
        cached_datasets,
        datasets,
        priority_display,
        setHovered,
        sync_id,
        selection_range,
        onChartMouseDown,
        onChartMouseMove
      } = t1;
      const highest_y_value = !props.datasets_type || props.datasets_type === "stacked" ? cached_datasets?.map(_temp).reduce(_temp2, 0) || 0 : cached_datasets?.map(_temp3).reduce(_temp4, 0) || 0;
      const y_axis_ticks = getYAxisTicks({
        data_max: highest_y_value,
        allow_decimals: false
      });
      const y_axis_width_value = y_axis_ticks.reduce(_temp5, 1);
      return <RechartBarChart margin={{
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      }} data={data} syncId={sync_id} syncMethod="index" accessibilityLayer={false} onMouseMove={onChartMouseMove} onMouseDown={onChartMouseDown} barCategoryGap="4%"><CartesianGrid strokeDasharray="3 3" stroke={theme.colors.outline.neutral} horizontalCoordinatesGenerator={t2 => {
          const {
            yAxis
          } = t2;
          return y_axis_ticks.map(tick_0 => yAxis?.scale?.map(tick_0)).filter(_temp6);
        }} vertical={false} /><Tooltip cursor={<ChartSampleCursor stroke={theme.colors.outline.neutral_3} />} content={_temp7} /><XAxis dataKey="name" allowDecimals={false} stroke={theme.colors.outline.neutral} tick={{
          fill: theme.colors.on.neutral.tertiary_neutral,
          fontSize: theme.font_sizes.xs.font_size
        }} tickMargin={8} minTickGap={16} ticks={priority_display} /><YAxis width={32 + y_axis_width_value * 8} allowDecimals={false} stroke="transparent" domain={[y_axis_ticks[0], y_axis_ticks[y_axis_ticks.length - 1]]} ticks={y_axis_ticks} interval={0} tickMargin={8} minTickGap={24} tick={{
          fill: theme.colors.on.neutral.tertiary_neutral,
          fontSize: theme.font_sizes.xs.font_size
        }} tickFormatter={formatChartAxisNumber} />{selection_range && <ReferenceArea x1={selection_range.start} x2={selection_range.end} fill={theme.colors.surface.base.hover.neutral} strokeOpacity={0} />}{datasets?.map(dataset => <Bar key={dataset.key} dataKey={dataset.label} isAnimationActive={false} stackId="a" shape={CustomBar({
          id,
          dataset,
          theme_colors: theme.colors
        })} onMouseEnter={e => {
          setHovered({
            key: dataset.key,
            label: dataset.label,
            name: e.name
          });
        }} onMouseLeave={() => setHovered(null)} />)}</RechartBarChart>;
    };
    $[0] = props.datasets_type;
    $[1] = theme.colors;
    $[2] = theme.font_sizes.xs.font_size;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== props || $[5] !== t0) {
    t1 = <Chart type="bar" {...props}>{t0}</Chart>;
    $[4] = props;
    $[5] = t0;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
};
export default BarChart;
function _temp(d) {
  return d.highest;
}
function _temp2(a, b) {
  return a + b;
}
function _temp3(d_0) {
  return d_0.highest;
}
function _temp4(a_0, b_0) {
  return Math.max(a_0, b_0);
}
function _temp5(width, tick) {
  return Math.max(width, formatChartAxisNumber(tick).length);
}
function _temp6(coordinate) {
  return Number.isFinite(coordinate);
}
function _temp7() {
  return null;
}